import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';

// Consulting layer (spec 11.1): the standing briefing. Reads the worker-generated daily packet
// or weekly control-meeting pack (latest). Read-only; the worker generates and persists it, the
// surface reads it with its as-of stamp.

interface AgendaItem { money_node_id: string; headline: string; severity: string }
interface AwaitingItem { drift_id: string; signal_name: string; severity: string }
interface EmergingItem { signal_name: string; steps_to_breach: number }
interface DailySection {
  agenda?: AgendaItem[];
  highest_dollar_decision?: AgendaItem | null;
  awaiting_disposition?: AwaitingItem[];
  emerging_watch?: EmergingItem[];
  changed_since_last?: {
    first_packet?: boolean;
    new_money_nodes?: string[];
    resolved_money_nodes?: string[];
    severity_changes?: { money_node_id: string; from: string; to: string }[];
  };
}
interface WeeklySection {
  learning_accuracy?: { graded_interventions: number; mean_accuracy_pct: number | null };
  assumptions_under_pressure?: { assumption_id: string; pressure: number; trend: string }[];
  ledger_movements?: {
    status_changes?: { id: string; from: string; to: string }[];
    priority_shifts?: { id: string; from: number; to: number }[];
  };
  weeks_decisions?: unknown[];
}
interface BriefingPayload { kind: string; as_of_date: string; daily?: DailySection; weekly?: WeeklySection | null }

export default function Briefing() {
  const [kind, setKind] = useState<'daily' | 'weekly'>('daily');
  const briefing = useQuery({ queryKey: ['briefing', kind], queryFn: () => api.briefingLatest(kind) });

  if (briefing.isLoading) return <div className="small faint">Loading briefing…</div>;
  if (briefing.isError || !briefing.data) return <div className="small faint">Briefing unavailable.</div>;

  const payload = briefing.data.payload as unknown as BriefingPayload;
  const daily = payload.daily ?? {};
  const weekly = payload.weekly ?? null;
  const changed = daily.changed_since_last;

  return (
    <div>
      <div className="ph">Consulting · Standing Briefing</div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="h2">{briefing.data.kind === 'weekly' ? 'Weekly control-meeting pack' : 'Daily packet'}</div>
        <div className="row" style={{ gap: 6 }}>
          {(['daily', 'weekly'] as const).map((k) => (
            <button key={k} type="button" className={`btn ${kind === k ? 'primary' : 'ghost'}`} style={{ minHeight: 'auto', padding: '6px 12px', fontSize: 13 }} onClick={() => setKind(k)}>
              {k}
            </button>
          ))}
        </div>
      </div>
      <p className="lead">As of {briefing.data.as_of_date} · generated {new Date(briefing.data.created_at).toLocaleString()}</p>

      {daily.highest_dollar_decision && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--red)' }} />Highest-$ decision</div>
          <div className="small" style={{ lineHeight: 1.6 }}>{daily.highest_dollar_decision.headline}</div>
        </div>
      )}

      <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Cross-engine agenda</div>
          {(daily.agenda ?? []).map((a) => (
            <div key={a.money_node_id} className="small" style={{ marginBottom: 8, lineHeight: 1.5 }}>
              <span className={`tag ${a.severity === 'existential' || a.severity === 'high' ? 'red' : 'muted'}`}>{a.severity}</span> {a.headline}
            </div>
          ))}
        </div>
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Awaiting disposition · {daily.awaiting_disposition?.length ?? 0}</div>
          {(daily.awaiting_disposition ?? []).map((d) => (
            <div key={d.drift_id} className="small" style={{ marginBottom: 6 }}><span className="tag amber">{d.severity}</span> {d.signal_name}</div>
          ))}
          <div className="qhead" style={{ marginTop: 14 }}><span className="qdot" style={{ background: 'var(--blue)' }} />Emerging watch · {daily.emerging_watch?.length ?? 0}</div>
          {(daily.emerging_watch ?? []).map((e, i) => (
            <div key={i} className="small" style={{ marginBottom: 6 }}>{e.signal_name} · ~{e.steps_to_breach} to breach</div>
          ))}
        </div>
      </div>

      {changed && !changed.first_packet && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />What changed since last packet</div>
          <div className="small">
            {changed.new_money_nodes?.length ? <div>New: {changed.new_money_nodes.join(', ')}</div> : null}
            {changed.resolved_money_nodes?.length ? <div>Resolved: {changed.resolved_money_nodes.join(', ')}</div> : null}
            {(changed.severity_changes ?? []).map((c) => (
              <div key={c.money_node_id}>{c.money_node_id}: {c.from} → <b>{c.to}</b></div>
            ))}
            {!changed.new_money_nodes?.length && !changed.resolved_money_nodes?.length && !changed.severity_changes?.length ? (
              <span className="faint">No agenda changes.</span>
            ) : null}
          </div>
        </div>
      )}

      {weekly && (
        <div className="grid2" style={{ gap: 14 }}>
          <div className="panel">
            <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Learning accuracy</div>
            <div className="small">{weekly.learning_accuracy?.graded_interventions ?? 0} graded · mean {weekly.learning_accuracy?.mean_accuracy_pct ?? '—'}%</div>
            <div className="qhead" style={{ marginTop: 14 }}><span className="qdot" style={{ background: 'var(--amber)' }} />Assumptions under pressure</div>
            {(weekly.assumptions_under_pressure ?? []).map((p) => (
              <div key={p.assumption_id} className="small" style={{ marginBottom: 4 }}>{p.assumption_id} · {p.trend} · {p.pressure}</div>
            ))}
          </div>
          <div className="panel">
            <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Ledger movements</div>
            {(weekly.ledger_movements?.status_changes ?? []).map((c) => (
              <div key={`s-${c.id}`} className="small" style={{ marginBottom: 4 }}>{c.id}: {c.from} → <b>{c.to}</b></div>
            ))}
            {(weekly.ledger_movements?.priority_shifts ?? []).map((p) => (
              <div key={`p-${p.id}`} className="small" style={{ marginBottom: 4 }}>{p.id}: priority {p.from} → <b>{p.to}</b></div>
            ))}
            {!(weekly.ledger_movements?.status_changes ?? []).length && !(weekly.ledger_movements?.priority_shifts ?? []).length ? (
              <div className="small faint">No ledger movements.</div>
            ) : null}
            <div className="qhead" style={{ marginTop: 14 }}><span className="qdot" style={{ background: 'var(--teal)' }} />Week's decisions</div>
            <div className="small">{weekly.weeks_decisions?.length ?? 0} logged this week</div>
          </div>
        </div>
      )}
    </div>
  );
}
